import React, { useRef, useEffect, useState } from 'react';
import styles from './HeroSection.module.scss';
import SectionBadge from '../components/SectionBadge';
import CTAButton from '../components/CTAButton';
import PartnerLogos from '../components/PartnerLogos';

const LazyVideo = ({ src, className }) => {
  const videoRef = useRef(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasLoaded) {
            setHasLoaded(true);
            video.load();
          }

          if (entry.isIntersecting && hasLoaded) {
            video.play().catch(() => {});
          } else if (!entry.isIntersecting) {
            video.pause();
          }
        });
      },
      { threshold: 0.2 }
    );

    observer.observe(video);
    return () => observer.disconnect();
  }, [hasLoaded]);

  return (
    <video
      ref={videoRef}
      className={className}
      controls
      loop
      playsInline
      preload="metadata"
    >
      <source src={src} type="video/mp4" />
    </video>
  );
};

const GiftIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path
      d="M16.6667 10V17.5H3.33337V10M10 17.5V6.66667M10 6.66667H5.83337C5.05462 6.66667 4.31379 6.34166 3.78087 5.80874C3.24795 5.27582 2.92212 4.53503 2.92212 3.75625C2.92212 2.97753 3.24795 2.23674 3.78087 1.70382C4.31379 1.1709 5.05462 0.845825 5.83337 0.845825C9.16671 0.845825 10 6.66667 10 6.66667M10 6.66667H14.1667C14.9455 6.66667 15.6863 6.34166 16.2192 5.80874C16.7521 5.27582 17.078 4.53503 17.078 3.75625C17.078 2.97753 16.7521 2.23674 16.2192 1.70382C15.6863 1.1709 14.9455 0.845825 14.1667 0.845825C10.8334 0.845825 10 6.66667 10 6.66667M1.66671 6.66667H18.3334V10H1.66671V6.66667Z"
      stroke="#92400E"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const InstagramIcon = () => (
  <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
    <rect width="44" height="44" rx="10" fill="url(#instagram-gradient)" />
    <path
      d="M22 15.5C18.4102 15.5 15.5 18.4102 15.5 22C15.5 25.5898 18.4102 28.5 22 28.5C25.5898 28.5 28.5 25.5898 28.5 22C28.5 18.4102 25.5898 15.5 22 15.5ZM22 26.5C19.5195 26.5 17.5 24.4805 17.5 22C17.5 19.5195 19.5195 17.5 22 17.5C24.4805 17.5 26.5 19.5195 26.5 22C26.5 24.4805 24.4805 26.5 22 26.5ZM29.5 15.25C29.5 15.9375 28.9375 16.5 28.25 16.5C27.5625 16.5 27 15.9375 27 15.25C27 14.5625 27.5625 14 28.25 14C28.9375 14 29.5 14.5625 29.5 15.25ZM32.9531 16.5156C32.875 14.8438 32.4883 13.3594 31.3125 12.1875C30.1406 11.0156 28.6562 10.6289 26.9844 10.5469C25.2617 10.4492 18.7344 10.4492 17.0117 10.5469C15.3438 10.625 13.8594 11.0117 12.6836 12.1836C11.5078 13.3555 11.125 14.8398 11.043 16.5117C10.9453 18.2344 10.9453 24.7617 11.043 26.4844C11.1211 28.1562 11.5078 29.6406 12.6836 30.8125C13.8594 31.9844 15.3398 32.3711 17.0117 32.4531C18.7344 32.5508 25.2617 32.5508 26.9844 32.4531C28.6562 32.375 30.1406 31.9883 31.3125 30.8125C32.4844 29.6406 32.8711 28.1562 32.9531 26.4844C33.0508 24.7617 33.0508 18.2383 32.9531 16.5156ZM30.5781 28.4609C30.2344 29.3281 29.5625 30 28.6914 30.3477C27.3242 30.8711 24.0625 30.7461 22 30.7461C19.9375 30.7461 16.6719 30.8672 15.3086 30.3477C14.4414 30.0039 13.7695 29.332 13.4219 28.4609C12.8984 27.0938 13.0234 23.832 13.0234 21.7695C13.0234 19.707 12.9023 16.4414 13.4219 15.0781C13.7656 14.2109 14.4375 13.5391 15.3086 13.1914C16.6758 12.668 19.9375 12.793 22 12.793C24.0625 12.793 27.3281 12.6719 28.6914 13.1914C29.5586 13.5352 30.2305 14.207 30.5781 15.0781C31.1016 16.4453 30.9766 19.707 30.9766 21.7695C30.9766 23.832 31.1016 27.0977 30.5781 28.4609Z"
      fill="white"
    />
    <defs>
      <linearGradient id="instagram-gradient" x1="0" y1="44" x2="44" y2="0" gradientUnits="userSpaceOnUse">
        <stop stopColor="#FFDC80" />
        <stop offset="0.5" stopColor="#F56040" />
        <stop offset="1" stopColor="#833AB4" />
      </linearGradient>
    </defs>
  </svg>
);

const LinkedInIcon = () => (
  <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
    <rect width="44" height="44" rx="10" fill="#0A66C2" />
    <path
      d="M16.5 18.5H12.5V31H16.5V18.5ZM14.5 16.5C15.8807 16.5 17 15.3807 17 14C17 12.6193 15.8807 11.5 14.5 11.5C13.1193 11.5 12 12.6193 12 14C12 15.3807 13.1193 16.5 14.5 16.5ZM32 31H28V24.75C28 23.2312 27.4688 22.25 26.25 22.25C25.0312 22.25 24.5 23.2312 24.5 24.75V31H20.5V18.5H24.5V20.25C24.5 20.25 25.5938 18 28.25 18C30.9062 18 32 20.0625 32 23.5V31Z"
      fill="white"
    />
  </svg>
);

const YouTubeIcon = () => (
  <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
    <rect width="44" height="44" rx="10" fill="#FF0000" />
    <path
      d="M33.5 16.5C33.5 16.5 33.2188 14.6562 32.4062 13.8438C31.3906 12.7812 30.25 12.7812 29.7188 12.7188C26.25 12.5 22 12.5 22 12.5C22 12.5 17.75 12.5 14.2812 12.7188C13.75 12.7812 12.6094 12.7812 11.5938 13.8438C10.7812 14.6562 10.5 16.5 10.5 16.5C10.5 16.5 10.2188 18.6562 10.2188 20.8125V22.8438C10.2188 25 10.5 27.1562 10.5 27.1562C10.5 27.1562 10.7812 29 11.5938 29.8125C12.6094 30.875 13.9375 30.8438 14.5312 30.9375C16.6875 31.1562 22 31.2188 22 31.2188C22 31.2188 26.25 31.2188 29.7188 30.9375C30.25 30.875 31.3906 30.875 32.4062 29.8125C33.2188 29 33.5 27.1562 33.5 27.1562C33.5 27.1562 33.7812 25 33.7812 22.8438V20.8125C33.7812 18.6562 33.5 16.5 33.5 16.5ZM19.5 25.5V17.5L26 21.5L19.5 25.5Z"
      fill="white"
    />
  </svg>
);

const XIcon = () => (
  <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
    <rect width="44" height="44" rx="10" fill="#000000" />
    <path
      d="M24.9 20.1L31.6 12H29.8L24.1 18.9L19.5 12H13L20.1 22.7L13 31H14.8L20.8 23.9L25.7 31H32L24.9 20.1ZM21.7 22.8L20.9 21.7L15.4 13.3H18.7L22.4 18.6L23.2 19.7L29.8 29.8H26.5L21.7 22.8Z"
      fill="white"
    />
  </svg>
);

const HeroSection = ({ onGetAccess }) => {
  return (
    <section className={styles.hero}>
      <div className={styles.backgroundPattern}>
        <div className={styles.grid} />
        <div className={styles.radialFade} />
      </div>

      <div className={styles.container}>
        <div className={styles.badge}>
          <SectionBadge icon={<GiftIcon />}>
            76% OFF WITH LIFETIME ACCESS - LIMITED TIME OFFER
          </SectionBadge>
        </div>

        <h1 className={styles.headline}>
          Grow your{' '}
          <span className={styles.socialIcons}>
            <InstagramIcon />
            <LinkedInIcon />
            <YouTubeIcon />
            <XIcon />
          </span>{' '}
          social media presence with{' '}
          <span className={styles.highlight}>strategic campaigns</span>
        </h1>

        <p className={styles.subheadline}>
          Creating high converting content across multiple platforms is hard and overwhelming. Vocable
          turns your message and expertise into multichannel content campaigns that grow your brand fast.
        </p>

        <div className={styles.cta}>
          <CTAButton onClick={onGetAccess}>
            Get lifetime access for $399 only
          </CTAButton>
        </div>

        <PartnerLogos />

        <div className={styles.heroImage}>
          <LazyVideo
            src="/Videos/vocable-overview.mp4"
            className={styles.screenshot}
          />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
